import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Plus, Search, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import AdressFelder from './AdressFelder';
import ProfilKacheln from './ProfilKacheln';
import { personSearchApi, type PersonSuggestion } from '../../lib/courses';
import { relationshipOptionsFor } from '../../lib/subprofileRelationships';
import KontaktwegeEditor from './KontaktwegeEditor';
import {
  LEERE_KUNDENDATEN,
  KONTAKT,
  STANDARD_REGELN,
  fehlendeKundenfelder,
  fehlendeSchuelerfelder,
  haupttelefon,
  kundeAlsFormular,
  kundenKontaktwege,
  stammdatenApi,
  type KundeUndProfilWert,
  type KundenDaten,
  type KundenTreffer,
  type ProfilZeile,
  type StammdatenRegeln,
} from '../../lib/api/stammdaten';
import { cn } from '../../lib/utils';

type Props = Readonly<{
  wert: KundeUndProfilWert;
  onChange: (wert: KundeUndProfilWert) => void;
  standortId?: number | null;
  /** Was aus der Anfrage bekannt ist — Name, E-Mail, Telefon. */
  ausAnfrage?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /** Zeigt die Lücken an; solange false, bleibt das Formular ruhig. */
  pruefen?: boolean;
}>;

const fehlerKlasse = 'border-destructive focus-visible:ring-destructive/40';

/** Was im Formular schon getippt wurde, überschreibt der Nachlader nicht. */
const leereFelderAuffuellen = (getippt: KundenDaten): Partial<KundenDaten> =>
  Object.fromEntries(
    Object.entries(getippt).filter(([, wert]) => wert !== '' && wert !== undefined)
  ) as Partial<KundenDaten>;

function Feld({
  label,
  htmlFor,
  fehlt,
  children,
}: Readonly<{ label: string; htmlFor: string; fehlt?: boolean; children: React.ReactNode }>) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {fehlt ? <p className="text-[11px] text-destructive">Pflichtfeld</p> : null}
    </div>
  );
}

function AnredeFeld({
  id,
  wert,
  onChange,
  fehlt,
  anreden,
}: Readonly<{ id: string; wert: string; onChange: (v: string) => void; fehlt: boolean; anreden: string[] }>) {
  return (
    <Feld label="Anrede *" htmlFor={id} fehlt={fehlt}>
      <select
        id={id}
        value={wert}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm',
          fehlt && fehlerKlasse
        )}
      >
        <option value="">Bitte auswählen</option>
        {anreden.map((anrede) => (
          <option key={anrede} value={anrede}>{anrede}</option>
        ))}
      </select>
    </Feld>
  );
}

/**
 * Kunde verknüpfen und Schülerprofil zuweisen — der Aufbau aus dem Kundentest.
 *
 * Aus T-0006/T-0007: Bisher stand hier ein Suchschlitz über alle Schülerprofile
 * des Mandanten, und wer neu anlegte, sah nicht, um wen es geht. Getrennt wird
 * jetzt, was fachlich getrennt ist: ein **Kunde** (Konto, Rechnungsanschrift)
 * bekommt ein **Schülerprofil** zugewiesen — ein bestehendes, ein neues, oder er
 * lernt selbst.
 *
 * Steht die E-Mail schon im System, wird der Kunde vorgeschlagen, statt ihn
 * suchen zu lassen.
 */
export default function KundeUndProfil({ wert, onChange, standortId, ausAnfrage, pruefen = false }: Props) {
  const [regeln, setRegeln] = useState<StammdatenRegeln>(STANDARD_REGELN);
  const [treffer, setTreffer] = useState<KundenTreffer | null>(null);
  const [sucheLaeuft, setSucheLaeuft] = useState(false);
  const [abgelehnt, setAbgelehnt] = useState(false);
  const [suchbegriff, setSuchbegriff] = useState('');
  const [suchtreffer, setSuchtreffer] = useState<PersonSuggestion[]>([]);
  // Die Profile des gefundenen Kunden, bis er übernommen ist. Bei einem Treffer
  // über die Adresse passiert das gleich von selbst, bei einem über die
  // Telefonnummer erst nach der Rückfrage.
  const [gefundeneProfile, setGefundeneProfile] = useState<ProfilZeile[]>([]);
  // Der Knopf „Profil hinzufügen" prüft für sich, auch wenn der Assistent noch
  // gar nicht abschicken wollte.
  const [eigenePruefung, setEigenePruefung] = useState(false);

  useEffect(() => {
    let aktuell = true;
    stammdatenApi
      .regeln(standortId)
      .then((geladen) => {
        if (aktuell) setRegeln(geladen);
      })
      // Die Vorbelegung ist dieselbe Liste; ohne Antwort bleibt das Formular bedienbar.
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [standortId]);

  const setzen = useCallback(
    (teil: Partial<KundeUndProfilWert>) => onChange({ ...wert, ...teil }),
    [onChange, wert]
  );

  const kundenTreffer = useCallback(
    async (kontakt: { email?: string; phone?: string; personId?: number }) => {
      setSucheLaeuft(true);
      try {
        const antwort = await stammdatenApi.kundeZuKontakt(kontakt);
        setTreffer(antwort.kunde);
        return antwort;
      } catch {
        setTreffer(null);
        return { kunde: null, profile: [] as ProfilZeile[] };
      } finally {
        setSucheLaeuft(false);
      }
    },
    []
  );

  // Die Anfrage bringt eine E-Mail mit — danach wird einmal gesucht, nicht bei
  // jedem Tastendruck.
  useEffect(() => {
    const email = (ausAnfrage?.email || '').trim();
    const phone = (ausAnfrage?.phone || '').trim();
    if (!email && !phone) return;
    void kundenTreffer({ email, phone }).then((antwort) => {
      setGefundeneProfile(antwort.profile);
      // Trifft die **Adresse** der Anfrage genau einen Kunden, ist er gemeint —
      // dann wird er gleich übernommen. Vorher musste man erst „Diesen Kunden
      // nehmen" drücken, und bis dahin blieben seine Schülerprofile unsichtbar:
      // Die Kacheln zeigten nur „Der Kunde selbst" und „Neues Profil", obwohl
      // längst weitere Profile am Konto hingen (T-0045, T-0049). Wer an den
      // Kacheln vorbeiscrollte, sah den Hinweis nie.
      //
      // Bei einem Treffer allein über die Telefonnummer bleibt der Hinweis: Eine
      // Nummer teilt sich eine Familie, da wäre das Übernehmen geraten.
      if (antwort.kunde && antwort.kunde.trefferAuf.includes('email')) {
        void uebernehmen(antwort.kunde, antwort.profile);
      }
    });
    // `uebernehmen` haengt am aktuellen Wert und wuerde die Suche sonst bei jeder
    // Eingabe neu ausloesen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ausAnfrage?.email, ausAnfrage?.phone, kundenTreffer]);

  // Kommt der Aufrufer schon mit einem Kunden (Terminplan mit Lernendem im
  // Gepäck), fehlen dessen Stammdaten und Profile noch — einmal nachladen.
  useEffect(() => {
    if (wert.personId === null || wert.profile.length > 0) return;
    let aktuell = true;
    void kundenTreffer({ personId: wert.personId }).then((antwort) => {
      if (!aktuell || !antwort.kunde) return;
      onChange({
        ...wert,
        kunde: { ...kundeAlsFormular(antwort.kunde), ...leereFelderAuffuellen(wert.kunde) },
        profile: antwort.profile,
      });
    });
    return () => {
      aktuell = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wert.personId]);

  // Solange noch kein Kunde gewählt ist, füllt die Anfrage das Formular vor.
  useEffect(() => {
    if (wert.personId !== null) return;
    if (wert.kunde.firstName || wert.kunde.lastName || wert.kunde.email) return;
    if (!ausAnfrage) return;
    setzen({
      kunde: {
        ...wert.kunde,
        firstName: ausAnfrage.firstName || '',
        lastName: ausAnfrage.lastName || '',
        email: ausAnfrage.email || '',
        phone: ausAnfrage.phone || '',
      },
    });
    // Nur beim ersten Befüllen; danach gehört das Formular dem Bediener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ausAnfrage?.email]);

  const uebernehmen = useCallback(
    async (kunde: KundenTreffer, geladeneProfile: ProfilZeile[]) => {
      const lernende = geladeneProfile.filter((p) => !p.istKonto);
      setzen({
        personId: kunde.personId,
        kunde: kundeAlsFormular(kunde),
        // Hat der Kunde schon Lernende, ist keiner davon automatisch gemeint —
        // die Wahl trifft der Bediener an den Kacheln.
        profilWahl: lernende.length > 0 ? { art: 'neu' } : { art: 'konto' },
        profile: geladeneProfile,
      });
      setTreffer(kunde);
    },
    [setzen]
  );

  const kundeSuchen = useCallback(async (begriff: string) => {
    setSuchbegriff(begriff);
    if (begriff.trim().length < 2) {
      setSuchtreffer([]);
      return;
    }
    try {
      setSuchtreffer(await personSearchApi.search(begriff.trim(), 'customer', 8));
    } catch {
      setSuchtreffer([]);
    }
  }, []);

  const kundeLoesen = useCallback(() => {
    setzen({ personId: null, kunde: LEERE_KUNDENDATEN, profilWahl: { art: 'konto' }, profile: [] });
    setTreffer(null);
    setGefundeneProfile([]);
    setAbgelehnt(true);
  }, [setzen]);

  const kundenLuecken = useMemo(
    () => (pruefen ? fehlendeKundenfelder(wert.kunde) : []),
    [pruefen, wert.kunde]
  );
  const schuelerLuecken = useMemo(
    () =>
      (pruefen || eigenePruefung) && wert.profilWahl.art === 'neu'
        ? fehlendeSchuelerfelder(wert.schueler, wert.kunde.phone, wert.kunde.email)
        : [],
    [pruefen, eigenePruefung, wert.profilWahl.art, wert.schueler, wert.kunde.phone]
  );

  // Das hinzugefügte, noch nicht gespeicherte Profil — es bleibt als Kachel
  // stehen, auch wenn gerade eine andere Kachel gewählt ist (T-0044).
  const entwurfName = [wert.schueler.firstName, wert.schueler.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const profilBestaetigt = wert.profilWahl.art === 'neu' && wert.profilWahl.bestaetigt === true;
  const entwurf = (profilBestaetigt || wert.entwurfFest) && entwurfName
    ? { name: entwurfName, birthDate: wert.schueler.birthDate || null }
    : null;

  /** „Hinzufügen": prüft die Pflichtangaben und klappt das Formular zu. */
  const profilUebernehmen = () => {
    const luecken = fehlendeSchuelerfelder(wert.schueler, wert.kunde.phone, wert.kunde.email);
    if (luecken.length > 0) {
      // Dieselben Felder rot wie beim Abschicken — der Knopf soll nicht nur
      // stumm nichts tun.
      setEigenePruefung(true);
      return;
    }
    setEigenePruefung(false);
    setzen({ profilWahl: { art: 'neu', bestaetigt: true }, entwurfFest: true });
  };

  const kundeFehlt = (feld: string) => kundenLuecken.includes(feld);
  const schuelerFehlt = (feld: string) => schuelerLuecken.includes(feld);
  const bestehenderKunde = wert.personId !== null;
  const vorschlagOffen = treffer !== null && !bestehenderKunde && !abgelehnt;

  return (
    <div className="grid gap-5">
      <section className="grid gap-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Kunde</h3>
          {bestehenderKunde ? (
            <Button type="button" variant="ghost" size="sm" onClick={kundeLoesen}>
              Anderen Kunden wählen
            </Button>
          ) : null}
        </div>

        {vorschlagOffen ? (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
            <div className="text-sm font-medium">
              Diesen Kunden gibt es schon
              {treffer?.trefferAuf.includes('email') ? ' — gleiche E-Mail-Adresse' : ' — gleiche Telefonnummer'}
            </div>
            <div className="mt-1 text-sm">
              {treffer?.name}
              {treffer?.email ? <span className="text-muted-foreground"> · {treffer.email}</span> : null}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={() => treffer && void uebernehmen(treffer, gefundeneProfile)}>
                <Check className="mr-1 h-4 w-4" aria-hidden /> Diesen Kunden nehmen
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setAbgelehnt(true)}>
                <UserPlus className="mr-1 h-4 w-4" aria-hidden /> Neuen Kunden anlegen
              </Button>
            </div>
          </div>
        ) : null}

        {!bestehenderKunde && !vorschlagOffen ? (
          <div className="grid gap-2">
            <Label htmlFor="kunde-suche">Bestehenden Kunden suchen</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                id="kunde-suche"
                className="pl-9"
                placeholder="Name, E-Mail oder Telefon"
                value={suchbegriff}
                onChange={(e) => void kundeSuchen(e.target.value)}
              />
            </div>
            {suchtreffer.length > 0 ? (
              <div className="max-h-44 overflow-auto rounded-md border">
                {suchtreffer.map((kandidat) => (
                  <button
                    key={String(kandidat.id)}
                    type="button"
                    className="w-full border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                    onClick={() => {
                      setSuchtreffer([]);
                      setSuchbegriff('');
                      void kundenTreffer({ email: String(kandidat.email || '') }).then((antwort) => {
                        if (antwort.kunde) void uebernehmen(antwort.kunde, antwort.profile);
                      });
                    }}
                  >
                    <div className="font-medium">{kandidat.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {kandidat.email || '—'} · {kandidat.phone || '—'}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
            <p className="text-[11px] text-muted-foreground">
              Kein Treffer? Dann unten einfach die Daten des neuen Kunden eintragen.
            </p>
          </div>
        ) : null}

        {sucheLaeuft ? <p className="text-xs text-muted-foreground">Kunde wird gesucht…</p> : null}

        <div className="grid gap-3 rounded-lg border p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Label htmlFor="kunde-firma">Firmenkunde</Label>
              <p className="text-[11px] text-muted-foreground">
                Rechnungen laufen dann auf die Firma statt auf die Person.
              </p>
            </div>
            <Switch
              id="kunde-firma"
              checked={wert.kunde.isCompany}
              onCheckedChange={(an) => setzen({ kunde: { ...wert.kunde, isCompany: an === true } })}
            />
          </div>
          {wert.kunde.isCompany ? (
            <Feld label="Firmenname *" htmlFor="kunde-firmenname" fehlt={kundeFehlt('Firmenname')}>
              <Input
                id="kunde-firmenname"
                className={cn(kundeFehlt('Firmenname') && fehlerKlasse)}
                value={wert.kunde.companyName}
                onChange={(e) => setzen({ kunde: { ...wert.kunde, companyName: e.target.value } })}
              />
            </Feld>
          ) : null}

          <AnredeFeld
            id="kunde-anrede"
            wert={wert.kunde.salutation}
            onChange={(v) => setzen({ kunde: { ...wert.kunde, salutation: v } })}
            fehlt={kundeFehlt('Anrede')}
            anreden={regeln.anreden}
          />

          <div className="grid gap-3 md:grid-cols-2">
            <Feld label="Vorname *" htmlFor="kunde-vorname" fehlt={kundeFehlt('Vorname')}>
              <Input
                id="kunde-vorname"
                className={cn(kundeFehlt('Vorname') && fehlerKlasse)}
                value={wert.kunde.firstName}
                onChange={(e) => setzen({ kunde: { ...wert.kunde, firstName: e.target.value } })}
              />
            </Feld>
            <Feld label="Nachname *" htmlFor="kunde-nachname" fehlt={kundeFehlt('Nachname')}>
              <Input
                id="kunde-nachname"
                className={cn(kundeFehlt('Nachname') && fehlerKlasse)}
                value={wert.kunde.lastName}
                onChange={(e) => setzen({ kunde: { ...wert.kunde, lastName: e.target.value } })}
              />
            </Feld>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Feld label="E-Mail (Konto)" htmlFor="kunde-email" fehlt={kundeFehlt(KONTAKT)}>
              <Input
                id="kunde-email"
                type="email"
                value={wert.kunde.email}
                disabled={bestehenderKunde}
                onChange={(e) => setzen({ kunde: { ...wert.kunde, email: e.target.value } })}
              />
              {bestehenderKunde ? (
                <p className="text-[11px] text-muted-foreground">
                  Die Kontoadresse steht fest — sie ist der Zugang des Kunden.
                </p>
              ) : null}
            </Feld>
          </div>

          <Feld label="Kontaktwege" htmlFor="kunde-wege-kontakt" fehlt={kundeFehlt(KONTAKT)}>
            <KontaktwegeEditor
              idPrefix="kunde-wege"
              wege={kundenKontaktwege(wert.kunde)}
              fehlt={kundeFehlt(KONTAKT)}
              onChange={(wege) =>
                setzen({ kunde: { ...wert.kunde, communications: wege, phone: haupttelefon(wege) } })
              }
            />
          </Feld>

          <Feld label="Geburtsdatum" htmlFor="kunde-geburtstag">
            <Input
              id="kunde-geburtstag"
              type="date"
              value={wert.kunde.birthDate || ''}
              onChange={(e) => setzen({ kunde: { ...wert.kunde, birthDate: e.target.value } })}
            />
          </Feld>

          <AdressFelder
            wert={wert.kunde.structuredAddress}
            landSichtbar={regeln.landSichtbar}
            fehlend={kundenLuecken}
            onChange={(adresse) => setzen({ kunde: { ...wert.kunde, structuredAddress: adresse } })}
          />
          <p className="text-[11px] text-muted-foreground">
            Für die Probestunde reicht E-Mail oder Telefon. Die Anschrift wird erst für die Anmeldung gebraucht.
          </p>
        </div>
      </section>

      <section className="grid gap-3">
        <div>
          <h3 className="text-sm font-semibold">Schülerprofil</h3>
          <p className="text-[11px] text-muted-foreground">
            Wer nimmt den Unterricht? Der Kunde selbst, ein bereits angelegtes Profil, oder ein neues.
          </p>
        </div>

        <ProfilKacheln
          kundenName={[wert.kunde.firstName, wert.kunde.lastName].filter(Boolean).join(' ')}
          profile={wert.profile}
          wahl={wert.profilWahl}
          onWahl={(wahl) => setzen({ profilWahl: wahl })}
          entwurf={entwurf}
        />

        {wert.profilWahl.art === 'neu' && !profilBestaetigt ? (
          <div className="grid gap-3 rounded-lg border p-3">
            <AnredeFeld
              id="schueler-anrede"
              wert={wert.schueler.salutation}
              onChange={(v) =>
                setzen({
                  schueler: {
                    ...wert.schueler,
                    salutation: v,
                    // Passt die Beziehung nicht mehr zur Anrede, wird sie geleert (T-0306).
                    ...(wert.schueler.relationshipType
                    && !relationshipOptionsFor(v).some((o) => o.value === wert.schueler.relationshipType)
                      ? { relationshipType: '', relationshipCustom: '' }
                      : {}),
                  },
                })
              }
              fehlt={schuelerFehlt('Anrede')}
              anreden={regeln.anreden}
            />
            <div className="grid gap-3 md:grid-cols-2">
              <Feld label="Vorname *" htmlFor="schueler-vorname" fehlt={schuelerFehlt('Vorname')}>
                <Input
                  id="schueler-vorname"
                  className={cn(schuelerFehlt('Vorname') && fehlerKlasse)}
                  value={wert.schueler.firstName}
                  onChange={(e) => setzen({ schueler: { ...wert.schueler, firstName: e.target.value } })}
                />
              </Feld>
              <Feld label="Nachname *" htmlFor="schueler-nachname" fehlt={schuelerFehlt('Nachname')}>
                <Input
                  id="schueler-nachname"
                  className={cn(schuelerFehlt('Nachname') && fehlerKlasse)}
                  value={wert.schueler.lastName}
                  onChange={(e) => setzen({ schueler: { ...wert.schueler, lastName: e.target.value } })}
                />
              </Feld>
            </div>
            <Feld label="Geburtsdatum *" htmlFor="schueler-geburtstag" fehlt={schuelerFehlt('Geburtsdatum')}>
              <Input
                id="schueler-geburtstag"
                type="date"
                className={cn(schuelerFehlt('Geburtsdatum') && fehlerKlasse)}
                value={wert.schueler.birthDate}
                onChange={(e) => setzen({ schueler: { ...wert.schueler, birthDate: e.target.value } })}
              />
            </Feld>
            <Feld label="Beziehung zum Kunden" htmlFor="schueler-beziehung">
              <select
                id="schueler-beziehung"
                className="h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                value={wert.schueler.relationshipType || ''}
                onChange={(e) =>
                  setzen({
                    schueler: {
                      ...wert.schueler,
                      relationshipType: e.target.value,
                      relationshipCustom: e.target.value === 'sonstiges' ? wert.schueler.relationshipCustom : '',
                    },
                  })
                }
              >
                <option value="">Bitte auswählen</option>
                {relationshipOptionsFor(wert.schueler.salutation).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Feld>
            {wert.schueler.relationshipType === 'sonstiges' ? (
              <Feld label="Beziehung" htmlFor="schueler-beziehung-frei">
                <Input
                  id="schueler-beziehung-frei"
                  placeholder="z. B. Nichte"
                  value={wert.schueler.relationshipCustom || ''}
                  onChange={(e) => setzen({ schueler: { ...wert.schueler, relationshipCustom: e.target.value } })}
                />
              </Feld>
            ) : null}

            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="schueler-kontakt">Kontaktwege des Kunden übernehmen</Label>
                <p className="text-[11px] text-muted-foreground">
                  Aus bedeutet: dieses Profil bekommt eine eigene Telefonnummer.
                </p>
              </div>
              <Switch
                id="schueler-kontakt"
                checked={wert.schueler.kontaktUebernehmen}
                onCheckedChange={(an) =>
                  setzen({ schueler: { ...wert.schueler, kontaktUebernehmen: an === true } })
                }
              />
            </div>
            {!wert.schueler.kontaktUebernehmen ? (
              <Feld label="Kontaktwege *" htmlFor="schueler-wege-kontakt" fehlt={schuelerFehlt(KONTAKT)}>
                <KontaktwegeEditor
                  idPrefix="schueler-wege"
                  wege={wert.schueler.communications || []}
                  fehlt={schuelerFehlt(KONTAKT)}
                  onChange={(wege) => setzen({ schueler: { ...wert.schueler, communications: wege } })}
                />
              </Feld>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
              <p className="text-[11px] text-muted-foreground">
                Hinzufügen stellt das Profil zur Auswahl; angelegt wird es am Ende.
              </p>
              <Button type="button" size="sm" onClick={profilUebernehmen}>
                <Plus className="mr-1 h-4 w-4" aria-hidden /> Profil hinzufügen
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
